import React, { useState, useEffect } from "react";

import { useGetAnnouncementQuery } from "../../api/tenantApi";
import {
  ChevronLeft,
  ChevronRight,
  Close,
  Notifications,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { setIsAnnouncementOpen } from "../../store/reducers/notificationsSlice";

const AnnouncementBanner = () => {
  const { currentData } = useGetAnnouncementQuery();
  const { isAnnouncementOpen } = useSelector((state) => state.notification);
  const dispatch = useDispatch();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [autoRotate, setAutoRotate] = useState(true);

  useEffect(() => {
    if (currentData?.data?.length) {
      dispatch(setIsAnnouncementOpen(true));
    }
  }, [currentData?.data]);

  const sortedAnnouncements = React.useMemo(() => {
    if (!currentData?.data) return [];
    return [...currentData.data].sort((a, b) => b.sortOrder - a.sortOrder);
  }, [currentData?.data]);

  useEffect(() => {
    if (!autoRotate || sortedAnnouncements.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % sortedAnnouncements.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [autoRotate, sortedAnnouncements.length]);

  const nextAnnouncement = () => {
    setAutoRotate(false);
    setCurrentIndex((prev) => (prev + 1) % sortedAnnouncements.length);
  };

  const prevAnnouncement = () => {
    setAutoRotate(false);
    setCurrentIndex(
      (prev) =>
        (prev - 1 + sortedAnnouncements.length) % sortedAnnouncements.length
    );
  };

  const getAnnouncementStyle = (colorCode) => {
    const color = colorCode?.toLowerCase();
    return {
      bg: `bg-[${color}]`,
      text: "text-white",
      icon: Notifications,
      iconColor: "text-blue-200",
    };
  };

  if (!isAnnouncementOpen || sortedAnnouncements.length === 0) return null;

  const currentAnnouncement = sortedAnnouncements[currentIndex];
  const style = getAnnouncementStyle(sortedAnnouncements?.priorityColourCode);
  const IconComponent = style.icon;

  return (
    <div
      style={{
        backgroundColor: currentAnnouncement?.priorityColourCode,
      }}
      className={`${style.text} px-4 py-3 shadow-lg w-full overflow-hidden fixed top-0 z-[999]`}
    >
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent transform -skew-x-12 animate-pulse"></div>
      </div>

      <div className="relative flex justify-between">
        <div className="flex items-center flex-1 min-w-0">
          <div className="flex-shrink-0 mr-3">
            <IconComponent className={`w-5 h-5 ${style?.iconColor}`} />
          </div>

          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate md:text-base">
              {currentAnnouncement?.text}
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-2 ml-4 justify-end">
          {currentData?.data.length > 1 && (
            <div className="hidden sm:flex items-center space-x-1">
              <span className="text-xs opacity-75">
                {currentIndex + 1} of {currentData?.data?.length}
              </span>
            </div>
          )}

          {currentData?.data.length > 1 && (
            <div className="flex items-center space-x-1">
              <button
                onClick={prevAnnouncement}
                className="p-1 rounded-full hover:bg-white hover:bg-opacity-20 transition-all duration-200 transform hover:scale-110"
                aria-label="Previous announcement"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              <button
                onClick={nextAnnouncement}
                className="p-1 rounded-full hover:bg-white hover:bg-opacity-20 transition-all duration-200 transform hover:scale-110"
                aria-label="Next announcement"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {currentData?.data.length > 1 && (
            <button
              onClick={() => setAutoRotate(!autoRotate)}
              className={`p-1 rounded-full transition-all duration-200 ${
                autoRotate
                  ? "bg-white bg-opacity-20 text-white"
                  : "hover:bg-white hover:bg-opacity-10"
              }`}
              aria-label={
                autoRotate ? "Pause auto-rotation" : "Resume auto-rotation"
              }
            >
              <div className="w-4 h-4 flex items-center justify-center">
                {autoRotate ? (
                  <div className="w-2 h-2 bg-current rounded-full animate-pulse"></div>
                ) : (
                  <div className="w-2 h-2 bg-current rounded-full opacity-50"></div>
                )}
              </div>
            </button>
          )}

          <button
            onClick={() => {
              localStorage.setItem("announcement", false);
              dispatch(setIsAnnouncementOpen(false))
            }}
            className="p-1 rounded-full hover:bg-white hover:bg-opacity-20 transition-all duration-200 transform hover:scale-110"
            aria-label="Close currentData?.data"
          >
            <Close className="w-4 h-4" />
          </button>
        </div>
      </div>

      {currentData?.data?.length > 1 && autoRotate && (
        <div className="absolute bottom-0 left-0 w-full h-1 bg-white bg-opacity-30">
          <div
            className="h-full bg-white transition-all duration-100 ease-linear"
            style={{
              width: "0%",
              animation: "progress 5s linear infinite",
            }}
          ></div>
        </div>
      )}

      <style jsx>{`
        @keyframes progress {
          from {
            width: 0%;
          }
          to {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default AnnouncementBanner;
